import { isolationContextId } from '@atlassian/atl-context';

import { isProdIsolatedCloud, isStagingIsolatedCloud } from '@adminhub/config';

export const FEATURE_KEY_ADMINHUB_USER_ACCESS_INVITES = 'AdminHub.UserAccessInvites';
export const FEATURE_KEY_ADMINHUB_USER_ACCESS_SETTINGS = 'AdminHub.UserAccessSettings';
export const FEATURE_KEY_ADMINHUB_SITE_RENAME = 'AdminHub.SiteRename';
export const FEATURE_KEY_ADMINHUB_MANAGED_ACCOUNTS_EXPORT = 'AdminHub.ManagedAccountsExport';
export const FEATURE_KEY_ADMINHUB_APPLICATION_TUNNELS = 'AdminHub.ApplicationTunnels';
export const FEATURE_KEY_ADMINHUB_USER_LAST_ACTIVE_TIMESTAMP = 'AdminHub.UserLastActiveTimestamp';
export const FEATURE_KEY_ADMINHUB_ADD_PRODUCT = 'AdminHub.AddProduct';
export const FEATURE_KEY_ADMINHUB_LINK_BITBUCKET = 'AdminHub.LinkBitbucket';
export const FEATURE_KEY_ADMINHUB_PRODUCT_RECOMMENDATION = 'AdminHub.ProductRecommendation';
export const FEATURE_KEY_ADMINHUB_DATA_MANAGEMENT = 'AdminHub.DataManagement';
export const FEATURE_KEY_ADMINHUB_BILLING = 'AdminHub.Billing';
export const FEATURE_KEY_ADMINHUB_DISCOVER_NEW_PRODUCTS = 'AdminHub.DiscoverNewProducts';
export const FEATURE_KEY_ADMINHUB_SHADOW_IT = 'AdminHub.ShadowIt';
export const FEATURE_KEY_ADMINHUB_SANDBOX = 'AdminHub.Sandbox';
export const FEATURE_KEY_ADMINHUB_DATA_RESIDENCY = 'AdminHub.DataResidency';
export const FEATURE_KEY_ADMINHUB_CONNECTED_SOURCES = 'AdminHub.ConnectedSources';
export const FEATURE_KEY_ADMINHUB_BACKUP_AND_RESTORE = 'AdminHub.BackupAndRestore';
export const FEATURE_KEY_ADMINHUB_PRODUCT_LINKS = 'AdminHub.ProductLinks';
export const FEATURE_KEY_ADMINHUB_PRODUCT_INSIGHTS = 'AdminHub.ProductInsights';
export const FEATURE_KEY_ADMINHUB_SYSTEM_HEALTH = 'AdminHub.SystemHealth';
export const FEATURE_KEY_ADMINHUB_PERFORMANCE_MONITORING = 'AdminHub.PerformanceMonitoring';
export const FEATURE_KEY_ADMINHUB_PLATFORM_EXPERIENCES = 'AdminHub.PlatformExperiences';
export const FEATURE_KEY_ADMINHUB_DATA_TRANSFER = 'AdminHub.DataTransfer';
export const FEATURE_KEY_ADMINHUB_DELETE_ORGANIZATION = 'AdminHub.DeleteOrganization';
export const FEATURE_KEY_ADMINHUB_CUSTOM_DOMAIN = 'AdminHub.CustomDomain';
export const FEATURE_KEY_ADMINHUB_CONNECTED_EMOJI = 'AdminHub.ConnectedEmoji';
export const FEATURE_KEY_ADMINHUB_SITE_TRANSFER = 'AdminHub.SiteTransfer';
export const FEATURE_KEY_ADMINHUB_CONTACTS = 'AdminHub.Contacts';
export const FEATURE_KEY_ADMINHUB_USER_REQUESTS = 'AdminHub.UserRequests';
export const FEATURE_KEY_ADMINHUB_CUSTOM_DOMAIN_EMAIL_NOTIFICATION = 'AdminHub.CustomDomainEmailNotification';
export const FEATURE_KEY_ADMINHUB_SITE_ACCESS_REQUEST = 'AdminHub.SiteAccessRequest';
export const FEATURE_KEY_ADMINHUB_AI_ENABLED_APPS = 'AdminHub.AiEnabledApps';
export const FEATURE_KEY_ADMINHUB_ORG_CREATE = 'AdminHub.OrgCreate';
export const FEATURE_KEY_ADMINHUB_ADMIN_INSIGHTS = 'AdminHub.AdminInsights';
export const FEATURE_KEY_ADMINHUB_NESTED_SIDE_NAVIGATION = 'AdminHub.NestedSideNavigation';
export const FEATURE_KEY_ADMINHUB_HELP = 'AdminHub.Help';

export type FeatureKey = string | undefined;

const ISOLATED_CLOUD_DISABLED_FEATURE_KEYS = new Set<FeatureKey>(
  [
    FEATURE_KEY_ADMINHUB_USER_ACCESS_INVITES,
    FEATURE_KEY_ADMINHUB_USER_ACCESS_SETTINGS,
    FEATURE_KEY_ADMINHUB_SITE_RENAME,
    FEATURE_KEY_ADMINHUB_MANAGED_ACCOUNTS_EXPORT,
    FEATURE_KEY_ADMINHUB_APPLICATION_TUNNELS,
    FEATURE_KEY_ADMINHUB_USER_LAST_ACTIVE_TIMESTAMP,
    FEATURE_KEY_ADMINHUB_ADD_PRODUCT,
    FEATURE_KEY_ADMINHUB_LINK_BITBUCKET,
    FEATURE_KEY_ADMINHUB_PRODUCT_RECOMMENDATION,
    FEATURE_KEY_ADMINHUB_DATA_MANAGEMENT,
    FEATURE_KEY_ADMINHUB_BILLING,
    FEATURE_KEY_ADMINHUB_DISCOVER_NEW_PRODUCTS,
    FEATURE_KEY_ADMINHUB_SHADOW_IT,
    FEATURE_KEY_ADMINHUB_SANDBOX,
    FEATURE_KEY_ADMINHUB_DATA_RESIDENCY,
    FEATURE_KEY_ADMINHUB_CONNECTED_SOURCES,
    FEATURE_KEY_ADMINHUB_BACKUP_AND_RESTORE,
    FEATURE_KEY_ADMINHUB_DATA_TRANSFER,
    FEATURE_KEY_ADMINHUB_DELETE_ORGANIZATION,
    FEATURE_KEY_ADMINHUB_PRODUCT_LINKS,
    FEATURE_KEY_ADMINHUB_PRODUCT_INSIGHTS,
    FEATURE_KEY_ADMINHUB_SYSTEM_HEALTH,
    FEATURE_KEY_ADMINHUB_PERFORMANCE_MONITORING,
    FEATURE_KEY_ADMINHUB_CUSTOM_DOMAIN,
    FEATURE_KEY_ADMINHUB_CONNECTED_EMOJI,
    FEATURE_KEY_ADMINHUB_SITE_TRANSFER,
    FEATURE_KEY_ADMINHUB_CONTACTS,
    FEATURE_KEY_ADMINHUB_USER_REQUESTS,
    FEATURE_KEY_ADMINHUB_CUSTOM_DOMAIN_EMAIL_NOTIFICATION,
    FEATURE_KEY_ADMINHUB_SITE_ACCESS_REQUEST,
    FEATURE_KEY_ADMINHUB_AI_ENABLED_APPS,
    FEATURE_KEY_ADMINHUB_ORG_CREATE,
    FEATURE_KEY_ADMINHUB_ADMIN_INSIGHTS,
    FEATURE_KEY_ADMINHUB_NESTED_SIDE_NAVIGATION,
    FEATURE_KEY_ADMINHUB_HELP,
  ].filter((key): key is string => key !== undefined),
);

const ISOLATED_CLOUD_STAGING_TEST_FEATURE_KEYS = new Set<FeatureKey>(
  [
    FEATURE_KEY_ADMINHUB_BILLING,
    FEATURE_KEY_ADMINHUB_HELP,
    FEATURE_KEY_ADMINHUB_PLATFORM_EXPERIENCES,
    FEATURE_KEY_ADMINHUB_CUSTOM_DOMAIN_EMAIL_NOTIFICATION,
    FEATURE_KEY_ADMINHUB_DATA_TRANSFER,
    FEATURE_KEY_ADMINHUB_DATA_MANAGEMENT,
    FEATURE_KEY_ADMINHUB_ADMIN_INSIGHTS,
  ].filter((key): key is string => key !== undefined),
);

const ISOLATED_CLOUD_PREPROD_TEST_FEATURE_KEYS = new Set<FeatureKey>(
  [FEATURE_KEY_ADMINHUB_PLATFORM_EXPERIENCES, FEATURE_KEY_ADMINHUB_DATA_TRANSFER, FEATURE_KEY_ADMINHUB_DATA_MANAGEMENT].filter(
    (key): key is string => key !== undefined,
  ),
);

interface FeatureContext {
  featureKey: FeatureKey;
}

export function isFeatureDisabled(context: FeatureContext): boolean {
  if (isStagingIsolatedCloud()) {
    if (ISOLATED_CLOUD_STAGING_TEST_FEATURE_KEYS.has(context.featureKey)) {
      return false;
    }

    return ISOLATED_CLOUD_DISABLED_FEATURE_KEYS.has(context.featureKey);
  }

  // pre-prod IC, which replaces staging IC
  if (isolationContextId() === 'ic-dr6') {
    if (ISOLATED_CLOUD_PREPROD_TEST_FEATURE_KEYS.has(context.featureKey)) {
      return false;
    }
  }

  if (isProdIsolatedCloud()) {
    return ISOLATED_CLOUD_DISABLED_FEATURE_KEYS.has(context.featureKey);
  }

  return false;
}
